// ==================== COMMISSION TABLE ====================
// One reseller's commission rows. The super admin's copy (resellerId given) can select
// pending rows and mark them paid; the reseller's own dashboard is read-only.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Input, Pagination, Select } from '../ui';
import type { ApiResponse, PaginatedResponse } from '../../types';
import { resellersService, type Commission, type CommissionQuery } from '../../services/resellers.service';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { formatPercent, paymentTypeLabel } from './labels';

interface CommissionTableProps {
  loadCommissions: (query: CommissionQuery) => Promise<ApiResponse<PaginatedResponse<Commission>>>;
  /** Super admin only: enables selecting pending rows and Mark as Paid */
  resellerId?: string;
  onPaid?: () => void;
}

export const CommissionTable = ({ loadCommissions, resellerId, onPaid }: CommissionTableProps) => {
  const [rows, setRows] = useState<Commission[]>([]);
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef({ id: 0 });

  const load = useCallback(async () => {
    const request = ++latestRequest.current.id;
    setLoading(true);
    const response = await loadCommissions({
      status: (status === 'ALL' ? undefined : status) as CommissionQuery['status'],
      page,
      limit,
    });
    if (request !== latestRequest.current.id) return;
    if (response.success && response.data) {
      setRows(response.data.items);
      setMeta(response.data.meta);
      setError(null);
      setSelected([]);
    } else {
      setRows([]);
      setSelected([]);
      setError(response.message);
    }
    setLoading(false);
  }, [loadCommissions, status, page, limit]);

  useEffect(() => {
    const requestState = latestRequest.current;
    void Promise.resolve().then(load);
    return () => {
      requestState.id++;
    };
  }, [load]);

  const markPaid = async () => {
    if (!resellerId || !selected.length || loading) return;
    setSaving(true);
    setError(null);
    const response = await resellersService.markPaid(
      resellerId,
      selected,
      reference.trim() || undefined,
      note.trim() || undefined,
    );
    setSaving(false);
    if (response.success) {
      setReference('');
      setNote('');
      // Paid rows leave the Pending filter, so its later pages can become empty
      if (status === 'PENDING' && page !== 1) setPage(1);
      else await load();
      onPaid?.();
    } else {
      setError(response.message);
    }
  };

  const pending = rows.filter((row) => row.status === 'PENDING');

  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <Select
          label="Status"
          disabled={saving}
          value={status}
          options={[
            { value: 'ALL', label: 'All statuses' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'PAID', label: 'Paid' },
          ]}
          onChange={(value) => {
            if (String(value) === status && page === 1) return;
            setLoading(true);
            setStatus(String(value));
            setPage(1);
            setSelected([]);
          }}
        />
      </div>
      {error && (
        <p role="alert" className="text-sm text-(--color-error)">
          {error}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-(--color-muted-gray) border-b border-(--color-border-gray)">
            <tr>
              {resellerId && (
                <th className="p-3">
                  <Checkbox
                    aria-label="Select all pending commissions"
                    checked={pending.length > 0 && pending.every((row) => selected.includes(row.id))}
                    onChange={(event) => setSelected(event.target.checked ? pending.map((row) => row.id) : [])}
                    disabled={loading || saving || pending.length === 0}
                  />
                </th>
              )}
              {['Customer', 'Payment', 'Date', 'Amount paid', 'Rate', 'Commission', 'Status'].map((text) => (
                <th key={text} className="p-3 font-medium whitespace-nowrap">
                  {text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-border-gray)">
            {rows.map((row) => (
              <tr key={row.id} className="text-(--color-soft-gray)">
                {resellerId && (
                  <td className="p-3">
                    <Checkbox
                      aria-label={`Select ${row.businessName} commission`}
                      checked={selected.includes(row.id)}
                      disabled={loading || saving || row.status !== 'PENDING'}
                      onChange={(event) =>
                        setSelected((prev) =>
                          event.target.checked ? [...prev, row.id] : prev.filter((id) => id !== row.id),
                        )
                      }
                    />
                  </td>
                )}
                <td className="p-3 text-white">{row.businessName}</td>
                <td className="p-3 whitespace-nowrap">{paymentTypeLabel(row.paymentType)}</td>
                <td className="p-3 whitespace-nowrap">{formatDate(row.paymentDate)}</td>
                <td className="p-3 whitespace-nowrap">{formatCurrency(Number(row.amountPaid), row.currency)}</td>
                <td className="p-3">{formatPercent(row.commissionPercent)}</td>
                <td className="p-3 whitespace-nowrap text-white font-medium">
                  {formatCurrency(Number(row.commissionAmount), row.currency)}
                </td>
                <td className="p-3">
                  <Badge variant={row.status === 'PAID' ? 'success' : 'warning'} size="sm">
                    {row.status === 'PAID' ? 'Paid' : 'Pending'}
                  </Badge>
                  {row.status === 'PAID' && (row.paidAt || row.paidReference) && (
                    <span className="block text-xs text-(--color-muted-gray) mt-1">
                      {[row.paidAt && formatDate(row.paidAt), row.paidReference].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={resellerId ? 8 : 7} className="p-8 text-center text-(--color-muted-gray)">
                  {loading ? 'Loading commissions...' : 'No commissions yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalPages={meta.totalPages}
        totalItems={meta.total}
        pageSize={limit}
        onPageChange={(value) => {
          if (value !== page) {
            setLoading(true);
            setPage(value);
          }
        }}
        onPageSizeChange={(value) => {
          if (value === limit && page === 1) return;
          setLoading(true);
          setLimit(value);
          setPage(1);
        }}
        itemLabel="commissions"
        isLoading={loading || saving}
      />
      {resellerId && (
        <div className="space-y-3 pt-4 border-t border-(--color-border-gray)">
          <p className="text-sm text-(--color-soft-gray)">
            {selected.length} pending commission{selected.length === 1 ? '' : 's'} selected
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Payment reference (optional)"
              maxLength={255}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              disabled={saving}
            />
            <Input
              label="Note (optional)"
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={saving}
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => void markPaid()}
              disabled={loading || saving || selected.length === 0}
              isLoading={saving}
            >
              Mark as Paid
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
